// src/hooks/useFraudLogs.js
// Firestore cursor-paginated hook for the Fraud Logs investigation page.
// Strategy:
//  • Base query: orderBy('createdAt', 'desc') + limit(PAGE_SIZE)
//  • Action / Phase filters → Firestore where() clauses (requires composite indexes)
//  • Date range → where('createdAt', >= / <=)
//  • Search by exact field → equality query, client-side pagination
//  • Min score + risk level → client-side filter applied after fetch
//  • Falls back gracefully if a composite index doesn't exist yet

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  collection,
  query,
  orderBy,
  limit,
  where,
  getDocs,
  startAfter,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../firebase';

export const PAGE_SIZE = 25;

// ─── Default filters ──────────────────────────────────────────────────────────
export const DEFAULT_FILTERS = {
  search: '',
  searchField: 'transactionId', // transactionId | uid | userEmail | destination
  action: 'ALL',
  phase: 'ALL',
  riskLevel: 'ALL',
  dateFrom: '',
  dateTo: '',
  minScore: '',
};

// ─── Helper: normalise reasons ────────────────────────────────────────────────
export const normaliseReasons = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.filter(Boolean);
  return [String(raw)];
};

// ─── Helper: parse any Firestore date ────────────────────────────────────────
export const parseDate = (val) => {
  if (!val) return null;
  if (typeof val.toDate === 'function') return val.toDate();
  if (val instanceof Date) return val;
  if (val.seconds) return new Date(val.seconds * 1000);
  if (typeof val === 'string') { const d = new Date(val); return isNaN(d) ? null : d; }
  return null;
};

// ─── Map a Firestore doc snapshot to a clean log object ──────────────────────
export const mapDoc = (snap) => {
  const d = snap.data();
  return {
    _id: snap.id,
    // Core fields
    uid: d.uid || d.userId || '',
    userEmail: d.userEmail || d.email || '',
    transactionId: d.transactionId || d.flowId || '',
    paymentIntent: d.paymentIntent || d.paymentIntentId || '',
    destination: d.destination || d.destinationPhone || d.phone || '',
    action: d.action || '',
    score: typeof d.score === 'number' ? d.score : null,
    phase: d.phase || '',
    reasons: normaliseReasons(d.reasons || d.reason || d.ruleTriggered),
    status: d.status || d.action || '',
    createdAt: parseDate(d.createdAt),
    // Stripe risk data (nested object)
    stripeRiskData: d.stripeRiskData || {},
    // Extra fields for the drawer
    paymentMethod: d.paymentMethod || d.payment_method || '',
    fraudDetails: d.fraudDetails || d.fraud_details || null,
    metadata: d.metadata || null,
    rawDoc: d, // full document for "raw" display in drawer
  };
};

// ─── Build the Firestore query constraints ────────────────────────────────────
const buildConstraints = (filters, cursor) => {
  const constraints = [];

  // ── Equality / range where clauses ──
  if (filters.action !== 'ALL') {
    constraints.push(where('action', '==', filters.action));
  }
  if (filters.phase !== 'ALL') {
    constraints.push(where('phase', '==', filters.phase));
  }

  // Date range
  if (filters.dateFrom) {
    const from = new Date(filters.dateFrom);
    from.setHours(0, 0, 0, 0);
    constraints.push(where('createdAt', '>=', Timestamp.fromDate(from)));
  }
  if (filters.dateTo) {
    const to = new Date(filters.dateTo);
    to.setHours(23, 59, 59, 999);
    constraints.push(where('createdAt', '<=', Timestamp.fromDate(to)));
  }

  // Sort
  constraints.push(orderBy('createdAt', 'desc'));

  // Cursor
  if (cursor) constraints.push(startAfter(cursor));

  // Page size — fetch slightly more so we can apply client-side filters
  constraints.push(limit(PAGE_SIZE * 3));

  return constraints;
};

// ─── Build search query ───────────────────────────────────────────────────────
const buildSearchConstraints = (field, term) => {
  // Exact equality search on indexed fields
  const firestoreField = {
    transactionId: 'transactionId',
    uid: 'uid',
    userEmail: 'userEmail',
    destination: 'destination',
  }[field] || 'transactionId';

  return [
    where(firestoreField, '==', term),
    orderBy('createdAt', 'desc'),
    limit(200),
  ];
};

// ─── Client-side filter ───────────────────────────────────────────────────────
const clientFilter = (logs, filters) => {
  return logs.filter((log) => {
    // Min score
    if (filters.minScore !== '') {
      const min = parseInt(filters.minScore, 10);
      if (!isNaN(min) && (log.score === null || log.score < min)) return false;
    }
    // Risk level (nested in stripeRiskData)
    if (filters.riskLevel !== 'ALL') {
      const rl = (log.stripeRiskData?.riskLevel || log.stripeRiskData?.risk_level || '').toLowerCase();
      if (rl !== filters.riskLevel.toLowerCase()) return false;
    }
    return true;
  });
};

// ─── Main Hook ────────────────────────────────────────────────────────────────
const useFraudLogs = () => {
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(DEFAULT_FILTERS);
  const [logs, setLogs] = useState([]);
  const [page, setPage] = useState(1);
  const [cursors, setCursors] = useState([null]); // cursors[n] = lastDoc of page n
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [indexError, setIndexError] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Track latest fetch request id to avoid race conditions
  const fetchId = useRef(0);

  // ── Apply filters (triggers a fresh fetch from page 1) ─────────────────────
  const applyFilters = useCallback(() => {
    setCursors([null]);
    setPage(1);
    setAppliedFilters({ ...filters });
  }, [filters]);

  const resetFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
    setAppliedFilters(DEFAULT_FILTERS);
    setCursors([null]);
    setPage(1);
  }, []);

  // ── Fetch page ──────────────────────────────────────────────────────────────
  const fetchPage = useCallback(async (pageNum, cursor, activeFilters) => {
    const id = ++fetchId.current;
    setLoading(true);
    setError(null);
    setIndexError(false);

    try {
      let rawDocs = [];
      const isSearching = activeFilters.search.trim().length > 0;

      if (isSearching) {
        // ── Search mode: exact match + client-side page slice ──
        const term = activeFilters.search.trim();
        const searchQ = query(
          collection(db, 'fraud_logs'),
          ...buildSearchConstraints(activeFilters.searchField, term)
        );
        const snap = await getDocs(searchQ);
        rawDocs = snap.docs.map(mapDoc);
      } else {
        // ── Browse mode: cursor pagination ──
        const constraints = buildConstraints(activeFilters, cursor);
        const q = query(collection(db, 'fraud_logs'), ...constraints);
        const snap = await getDocs(q);

        // Store cursor for next page
        const lastSnap = snap.docs[snap.docs.length - 1] || null;
        if (id === fetchId.current) {
          setCursors((prev) => {
            const next = [...prev];
            next[pageNum] = lastSnap;
            return next;
          });
        }
        rawDocs = snap.docs.map(mapDoc);
      }

      if (id !== fetchId.current) return;

      // Apply client-side filters
      const filtered = clientFilter(rawDocs, activeFilters);

      if (isSearching) {
        // Client-side pagination for search results
        const start = (pageNum - 1) * PAGE_SIZE;
        const page = filtered.slice(start, start + PAGE_SIZE);
        setLogs(page);
        setHasMore(start + PAGE_SIZE < filtered.length);
        // Store total for search so we can paginate client-side
        if (id === fetchId.current) {
          setCursors([null, ...Array.from({ length: Math.ceil(filtered.length / PAGE_SIZE) }, (_, i) => filtered.slice(0, (i + 1) * PAGE_SIZE))]);
        }
      } else {
        const page = filtered.slice(0, PAGE_SIZE);
        setLogs(page);
        setHasMore(filtered.length > PAGE_SIZE || rawDocs.length >= PAGE_SIZE);
      }
    } catch (err) {
      if (id !== fetchId.current) return;
      console.error('[useFraudLogs] Fetch error:', err);
      // Detect missing index error from Firebase
      if (err.code === 'failed-precondition' || err.message?.includes('index')) {
        setIndexError(true);
        setError('A Firestore composite index is required for this filter combination. Falling back to simpler query.');
        // Retry with minimal constraints
        try {
          const fallbackQ = query(
            collection(db, 'fraud_logs'),
            orderBy('createdAt', 'desc'),
            limit(PAGE_SIZE)
          );
          const fallbackSnap = await getDocs(fallbackQ);
          if (id === fetchId.current) {
            setLogs(fallbackSnap.docs.map(mapDoc));
            setHasMore(fallbackSnap.size === PAGE_SIZE);
          }
        } catch (fallbackErr) {
          if (id === fetchId.current) setError(fallbackErr.message);
        }
      } else {
        setError(err.message || 'Failed to load fraud logs');
      }
    } finally {
      if (id === fetchId.current) setLoading(false);
    }
  }, []);

  // ── React to page changes ───────────────────────────────────────────────────
  useEffect(() => {
    const cursor = appliedFilters.search.trim() ? null : (cursors[page - 1] ?? null);
    fetchPage(page, cursor, appliedFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, appliedFilters]);

  // ── Row selection ───────────────────────────────────────────────────────────
  const openDrawer = useCallback((log) => {
    setSelectedLog(log);
    setDrawerOpen(true);
  }, []);

  const closeDrawer = useCallback(() => {
    setDrawerOpen(false);
    setTimeout(() => setSelectedLog(null), 300); // let animation finish
  }, []);

  const goNext = useCallback(() => {
    if (hasMore) setPage((p) => p + 1);
  }, [hasMore]);

  const goPrev = useCallback(() => {
    if (page > 1) setPage((p) => p - 1);
  }, [page]);

  return {
    // Filter state
    filters,
    setFilters,
    applyFilters,
    resetFilters,
    // Data
    logs,
    loading,
    error,
    indexError,
    // Pagination
    page,
    hasMore,
    goNext,
    goPrev,
    // Drawer
    selectedLog,
    drawerOpen,
    openDrawer,
    closeDrawer,
  };
};

export default useFraudLogs;
